import React from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import PromptModelFields from './PromptModelFields';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  model: string;
  onModelChange: (model: string) => void;
  promptText: string;
  onPromptChange: (text: string) => void;
  // While true, show a spinner instead of the fields (e.g. the prompt is still
  // being fetched). Distinct from `busy` (a mutation in flight).
  loading?: boolean;
  busy: boolean;
  error?: string | null;
  confirmLabel: string;
  // Label shown next to the spinner while busy. Defaults to "Generating…".
  busyLabel?: string;
  // Extra disable condition beyond busy/loading/empty-prompt (e.g. missing config).
  confirmDisabled?: boolean;
  onConfirm: () => void;
}

// The single shared modal for both the "Re-generate" flow (clones into a new
// script) and the "Generate" quick-action (runs the existing script). The two
// are visually identical — same model picker + editable prompt — so they share
// one dialog; only the title/description/confirm label and the onConfirm
// behavior differ.
export default function PromptModelDialog({
  open, onClose, title, description, model, onModelChange, promptText, onPromptChange,
  loading = false, busy, error, confirmLabel, busyLabel = 'Generating…', confirmDisabled = false, onConfirm,
}: Props) {
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{description}</Typography>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : (
          <PromptModelFields
            model={model}
            onModelChange={onModelChange}
            promptText={promptText}
            onPromptChange={onPromptChange}
            disabled={busy}
          />
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disabled={busy || loading || confirmDisabled || !promptText.trim()} onClick={onConfirm}>
          {busy ? <><CircularProgress size={14} sx={{ mr: 1 }} />{busyLabel}</> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
